import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import type { PageTab } from '~/components/granular/page-tabs/PageTabs';

export type DashboardTab = PageTab & { id: string };

export function useDashboardTabs(
  tabs: DashboardTab[],
  {
    storageKey,
    storageDefaultValue,
    disabledTabValues = [],
  }: { storageKey: string; storageDefaultValue: string; disabledTabValues?: Array<string> },
) {
  const { user } = useAuth0();
  const { pathname } = useLocation();

  const [localStorageSelectedTabId, setLocalStorageSelectedTabId] = useLocalStorage<string>(
    `${user?.sub}.${user?.org_id}.${storageKey}`,
    storageDefaultValue,
  );

  const lastSelectedTabId = disabledTabValues.includes(localStorageSelectedTabId)
    ? storageDefaultValue
    : localStorageSelectedTabId;

  useEffect(() => {
    const tabId = tabs.find(({ route }) => route === pathname)?.id;
    if (tabId) {
      setLocalStorageSelectedTabId(tabId);
    }
  }, [setLocalStorageSelectedTabId, pathname, tabs]);

  return useMemo(
    () => ({
      lastSelectedTab: tabs.find(({ id }) => id === lastSelectedTabId),
      tabsProps: { activeRoute: pathname, tabs },
    }),
    [tabs, pathname, lastSelectedTabId],
  );
}
